import { useUser } from '@groupthinkai/groupthink';
import { useCallback } from 'react';
import { useLocalStorage } from 'usehooks-ts';

type UserPreferences = Record<string, any>;

const getUserPreferencesKey = (userId?: string) => {
  return 'groupthink_user_preferences_' + (userId ?? 'noid');
};

/**
 * Clears user preferences localStorage for unauthenticated users
 * This should be called when a user logs out to ensure no unidentified preference data persists
 */
export const clearUserPreferences = () => {
  localStorage.removeItem(getUserPreferencesKey());
};

/**
 * Hook to manage user-specific preferences stored in localStorage
 *
 * @returns Object containing preferences, setter function, and utility methods
 */
export const useUserPreferences = () => {
  const { user } = useUser('me');
  const storageKey = getUserPreferencesKey(user?.id);

  const [preferences, setPreferences] = useLocalStorage<UserPreferences>(storageKey, {});

  /**
   * Updates a specific preference key with a new value
   *
   * @param key - The preference key to update
   * @param value - The new value for the preference
   */
  const updatePreference = useCallback(
    (key: string, value: any) => {
      setPreferences((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setPreferences]
  );

  /**
   * Gets a specific preference value with optional default
   *
   * @param key - The preference key to retrieve
   * @param defaultValue - Default value if preference doesn't exist
   * @returns The preference value or default
   */
  const getPreference = useCallback(
    (key: string, defaultValue?: any) => {
      return preferences[key] ?? defaultValue;
    },
    [preferences]
  );

  /**
   * Removes a specific preference key
   *
   * @param key - The preference key to remove
   */
  const removePreference = useCallback(
    (key: string) => {
      setPreferences((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        const { [key]: removed, ...rest } = prev;
        return rest;
      });
    },
    [setPreferences]
  );

  /**
   * Clears all user preferences
   */
  const clearPreferences = useCallback(() => {
    setPreferences({});
  }, [setPreferences]);

  return {
    preferences,
    setPreferences,
    updatePreference,
    getPreference,
    removePreference,
    clearPreferences,
  };
};
